import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import coinIcon from "@/assets/UI/coin.webp";

import {
  MarketplaceEmptyState,
  MarketplaceLoadingState,
} from "../../../components/marketplace/MarketplaceStates";
import VideoPlayer from "../../../components/video/VideoPlayer/VideoPlayer";
import { listMarketplaceProducts } from "../../../features/marketplace/marketplaceProductApi";
import {
  CATEGORY_LABELS,
  PRODUCT_CATEGORIES,
  SUBTYPE_LABELS,
} from "../../../features/marketplace/marketplaceConstants";
import styles from "./ClubMarketplaceListPage.module.scss";

const SORT_OPTIONS = {
  newest: "Newest",
  priceAsc: "Price: Low to high",
  priceDesc: "Price: High to low",
  titleAsc: "Title: A-Z",
};

const safeString = (value) => (typeof value === "string" ? value.trim() : "");

const getProductMediaSlides = (product) => {
  const imageSlides = (product?.listingImageUrls || [])
    .map((url) => safeString(url))
    .filter(Boolean)
    .map((url, index) => ({
      id: `img-${index}-${url}`,
      type: "image",
      src: url,
    }));

  const videoSlides = (product?.listingVideoIds || [])
    .map((videoId) => safeString(videoId))
    .filter(Boolean)
    .map((videoId, index) => ({
      id: `vid-${index}-${videoId}`,
      type: "video",
      videoId,
    }));

  return [...imageSlides, ...videoSlides];
};

const getSellerDisplayName = (product) => {
  const snap =
    product?.sellerSnapshot || product?.seller || product?.user || {};
  return (
    safeString(snap?.username) ||
    safeString(snap?.displayName) ||
    safeString(product?.sellerUsername) ||
    safeString(product?.username) ||
    "Seller"
  );
};

const getSellerAvatarUrl = (product) => {
  const snap =
    product?.sellerSnapshot || product?.seller || product?.user || {};
  return (
    safeString(snap?.avatarThumbUrl) ||
    safeString(snap?.avatarUrl) ||
    safeString(product?.sellerAvatarThumbUrl) ||
    safeString(product?.sellerAvatarUrl) ||
    ""
  );
};

function Marketplace3DCard({ product, onClick }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const slides = useMemo(() => getProductMediaSlides(product), [product]);
  const hasSlides = slides.length > 0;

  useEffect(() => {
    setActiveSlide(0);
  }, [product?._id]);

  const activeItem = hasSlides ? slides[activeSlide % slides.length] : null;
  const price = Number(product?.priceNoty ?? 0);
  const sellerName = getSellerDisplayName(product);
  const sellerAvatar = getSellerAvatarUrl(product);
  const sellerInitial = sellerName?.charAt(0)?.toUpperCase() || "S";

  const showPrev = (event) => {
    event.stopPropagation();
    if (!hasSlides || slides.length <= 1) return;
    setActiveSlide((current) => (current - 1 + slides.length) % slides.length);
  };

  const showNext = (event) => {
    event.stopPropagation();
    if (!hasSlides || slides.length <= 1) return;
    setActiveSlide((current) => (current + 1) % slides.length);
  };

  return (
    <article
      className={styles.product3dCard}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.cardGlowPink} />
      <div className={styles.cardGlowBlue} />
      <div className={styles.cardGridFx} />

      <div className={styles.mediaSquare}>
        {activeItem?.type === "image" ? (
          <img
            src={activeItem.src}
            alt={product?.title || "Marketplace item"}
          />
        ) : activeItem?.type === "video" ? (
          <VideoPlayer
            token={token}
            videoId={activeItem.videoId}
            mode="proxy"
            controls={false}
            muted
            autoPlay={false}
            className={styles.videoSlide}
          />
        ) : (
          <div className={styles.mediaPlaceholder}>No media</div>
        )}

        {slides.length > 1 ? (
          <>
            <button
              type="button"
              className={`${styles.slideNav} ${styles.slideNavPrev}`}
              onClick={showPrev}
              aria-label="Previous media"
            >
              ‹
            </button>
            <button
              type="button"
              className={`${styles.slideNav} ${styles.slideNavNext}`}
              onClick={showNext}
              aria-label="Next media"
            >
              ›
            </button>

            <div className={styles.slideDots}>
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  className={`${styles.dot} ${index === activeSlide ? styles.dotActive : ""}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveSlide(index);
                  }}
                  aria-label={`Go to media ${index + 1}`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      <div className={styles.cardMeta}>
        <h3>{product?.title || "Untitled listing"}</h3>
        <strong className={styles.priceWrap}>
          <span>{price}</span>
          <img
            src={coinIcon}
            alt=""
            aria-hidden="true"
            className={styles.priceIcon}
          />
        </strong>
      </div>

      <div className={styles.sellerStrip}>
        <span className={styles.sellerName}>By</span>
        <div className={styles.sellerAvatarWrap}>
          
          {sellerAvatar ? (
            <img
              src={sellerAvatar}
              alt={sellerName}
              className={styles.sellerAvatar}
            />
          ) : (
            <span className={styles.sellerAvatarFallback}>{sellerInitial}</span>
          )}
        </div>
        <span className={styles.sellerName}>{sellerName}</span>
      </div>
    </article>
  );
}

export default function ClubMarketplaceListPage() {
  const { clubId } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMemberBlocked, setIsMemberBlocked] = useState(false);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [subtype, setSubtype] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      setLoading(true);
      setIsMemberBlocked(false);

      try {
        const response = await listMarketplaceProducts(clubId, {
          scope: "club",
          limit: 50,
        });

        if (!isMounted) return;

        const approvedAndActive = (response?.products || []).filter(
          (item) =>
            item?.status === "active" &&
            (!item?.moderationStatus || item?.moderationStatus === "approved"),
        );

        setProducts(approvedAndActive);
      } catch (error) {
        if (!isMounted) return;

        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setIsMemberBlocked(true);
          setProducts([]);
          return;
        }

        setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, [clubId]);

  const subtypeOptions = useMemo(() => {
    const fromData = new Set();

    products.forEach((item) => {
      if (category !== "all" && item.category !== category) return;
      if (item.serviceType) fromData.add(item.serviceType);
      if (item.digitalType) fromData.add(item.digitalType);
    });

    return Array.from(fromData);
  }, [category, products]);

  const visibleProducts = useMemo(() => {
    const loweredSearch = search.trim().toLowerCase();

    const filtered = products.filter((item) => {
      const productSubtype = item.serviceType || item.digitalType || "";

      const matchesSearch =
        !loweredSearch ||
        item.title?.toLowerCase().includes(loweredSearch) ||
        item.description?.toLowerCase().includes(loweredSearch);

      const matchesCategory = category === "all" || item.category === category;
      const matchesSubtype = subtype === "all" || productSubtype === subtype;

      return matchesSearch && matchesCategory && matchesSubtype;
    });

    const sorted = [...filtered];

    if (sortBy === "priceAsc") {
      sorted.sort(
        (a, b) => Number(a.priceNoty || 0) - Number(b.priceNoty || 0),
      );
    } else if (sortBy === "priceDesc") {
      sorted.sort(
        (a, b) => Number(b.priceNoty || 0) - Number(a.priceNoty || 0),
      );
    } else if (sortBy === "titleAsc") {
      sorted.sort((a, b) =>
        String(a.title || "").localeCompare(String(b.title || "")),
      );
    } else {
      sorted.sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime(),
      );
    }

    return sorted;
  }, [category, products, search, sortBy, subtype]);

  if (isMemberBlocked) {
    return (
      <section className={styles.blockedWrap}>
        <div className={styles.blockedCard}>
          <div className={styles.blockedGlowPink} />
          <div className={styles.blockedGlowBlue} />
          <p className={styles.kicker}>Marketplace Access Locked</p>
          <h2>Join club to access marketplace</h2>
          <p>
            You need an active membership in this club before browsing
            marketplace listings.
          </p>
        </div>
      </section>
    );
  }

  if (loading) {
    return <MarketplaceLoadingState message="Loading club marketplace..." />;
  }

  return (
    <section className={styles.page}>
      <div className={styles.bgGlowPink} />
      <div className={styles.bgGlowBlue} />
      <div className={styles.bgGrid} />

      <header className={styles.hero}>
        <div className={styles.heroText}>
          <p className={styles.kicker}>Club Marketplace</p>
          <h2>Marketplace Listings</h2>
          <p className={styles.heroSub}>
            Approved and active products in this club.
          </p>
        </div>

        <div className={styles.heroStats}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Visible</span>
            <strong className={styles.statValue}>
              {visibleProducts.length}
            </strong>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Total</span>
            <strong className={styles.statValue}>{products.length}</strong>
          </div>
        </div>
      </header>

      <section className={styles.filtersPanel}>
        <div className={styles.filtersHeader}>
          <h3>Filter Console</h3>
          <p>Tune the listings like a game loadout.</p>
        </div>

        <div className={styles.filters}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Search</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search listings"
              className={styles.input}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Category</span>
            <select
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                setSubtype("all");
              }}
              className={styles.input}
            >
              <option value="all">All categories</option>
              {Object.values(PRODUCT_CATEGORIES).map((value) => (
                <option key={value} value={value}>
                  {CATEGORY_LABELS[value] || value}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Subtype</span>
            <select
              value={subtype}
              onChange={(event) => setSubtype(event.target.value)}
              className={styles.input}
            >
              <option value="all">All subtypes</option>
              {subtypeOptions.map((value) => (
                <option key={value} value={value}>
                  {SUBTYPE_LABELS[value] || value}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Sort by</span>
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className={styles.input}
            >
              {Object.entries(SORT_OPTIONS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {!visibleProducts.length ? (
        <div className={styles.stateWrap}>
          <MarketplaceEmptyState message="No approved products match your filters yet." />
        </div>
      ) : (
        <div className={styles.grid}>
          {visibleProducts.map((product) => (
            <Marketplace3DCard
              key={product._id}
              product={product}
              onClick={() => navigate(`products/${product._id}`)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
